import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { VirtuosoGrid } from 'react-virtuoso';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Api
import { GET_MEMORABILIA } from 'api/memorabilia/queries';
// Types
import {
  GetMemorabilia,
  GetMemorabiliaVariables,
  GetMemorabilia_getMemorabilia_entities,
} from 'api/memorabilia/types/GetMemorabilia';
import {
  MemorabiliaProductType,
  MemorabiliaStatus,
} from 'api/graphql-global-types';
import { SearchLocation } from 'api/graphql-analytics-global-types';
// Components
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
import { getMemorabiliaHoverImage } from 'helpers/products';
import NotFound from 'components/common/NotFound/NotFound';
import PageWithFiltersWrapper from 'components/common3/PageWithFiltersWrapper/PageWithFiltersWrapper';
import MemorabiliaListItem from 'components/common3/MemorabiliaList/components/MemorabiliaListItem';
// Styles
import styles from './AllMemorabilia.module.scss';

const PER_PAGE = 18;

const VirtuosoGridItem: React.FC<{
  item: GetMemorabilia_getMemorabilia_entities;
}> = React.memo(({ item }) => {
  const hoverImageUrl = getMemorabiliaHoverImage(item);
  return (
    <MemorabiliaListItem
      memorabilia={item}
      itemType="default"
      showBuyNowButton
      hoverImageUrl={hoverImageUrl}
    />
  );
});
VirtuosoGridItem.displayName = 'VirtuosoGridItem';

type AllMemorabiliaProps = {
  isPage?: boolean;
  noClear?: boolean;
  title?: string;
  storeSlug?: string | undefined | null;
  productType?: MemorabiliaProductType;
  singleCategory?: boolean;
};

export const AllMemorabilia = ({
  isPage = true,
  noClear = false,
  title,
  storeSlug,
  productType,
  singleCategory = false,
}: AllMemorabiliaProps) => {
  const { filters, sorting, searchKey, search } = useFiltersContext();
  const { query } = useRouter();

  const inputVariables = useMemo(
    () => ({
      orderBy: sorting.order,
      direction: sorting.direction,
      limit: PER_PAGE,
      offset: 0,
      statuses: [MemorabiliaStatus.Active],
      storeIds: [
        ...filters.athleteIds,
        ...filters.organizationIds,
        ...filters.contentCreatorIds,
      ],
      ...{ storeSlug },
      ...(filters?.priceRange?.length
        ? {
            price: {
              from: Number(filters?.priceRange[0]),
              to: Number(filters?.priceRange[1]),
            },
          }
        : {}),
      ...(filters?.sports?.length
        ? { sports: filters.sports.map((i) => Number(i)) }
        : {}),
      ...(searchKey &&
        search[searchKey] && {
          [searchKey]: search[searchKey],
        }),
      // first check for productType prop (used on Athlete page), then query, then filters
      ...(productType
        ? {
            productTypes: [productType],
          }
        : query?.productType
        ? {
            productTypes: (query?.productType as string).split(
              ','
            ) as MemorabiliaProductType[],
          }
        : filters?.productType?.length
        ? { productTypes: filters.productType }
        : {}),
      ...(filters?.saleMethod?.length
        ? { saleMethods: filters.saleMethod }
        : {}),
    }),
    [filters, sorting, searchKey, search, query, storeSlug, productType]
  );

  const { data, loading, fetchMore } = useQuery<
    GetMemorabilia,
    GetMemorabiliaVariables
  >(GET_MEMORABILIA, {
    variables: {
      input: {
        ...inputVariables,
      },
    },
    fetchPolicy: 'network-only',
  });

  const fetchMoreData = () => {
    const prevOffset = data?.getMemorabilia?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
        },
      },
    });
  };

  const handleOnEndReached = () => {
    if (hasMore) {
      fetchMoreData();
    }
  };

  const memorabilia = data?.getMemorabilia?.entities || [];

  const total = data?.getMemorabilia?.total;
  const hasMore = data ? memorabilia.length !== total : false;
  const isNotFound = !loading && total === 0;

  return (
    <PageWithFiltersWrapper
      sortKey="Products"
      title={title || 'Products'}
      loading={loading}
      filters={data?.getMemorabilia?.filters}
      isPage={isPage}
      noClear={noClear}
      searchBarLocation={
        title === 'Products'
          ? SearchLocation.Products
          : SearchLocation.Memorabilia
      }
      singleCategory={singleCategory}
      headerSpaceForMobile={false}
    >
      {isNotFound && <NotFound title="Product not found" />}

      {!!memorabilia.length && (
        <VirtuosoGrid
          useWindowScroll
          overscan={PER_PAGE * 5}
          data={memorabilia}
          listClassName={styles.root}
          totalCount={data?.getMemorabilia.total}
          itemContent={(index, item) => <VirtuosoGridItem item={item} />}
          endReached={handleOnEndReached}
          components={{
            Footer: () => {
              if (!hasMore) {
                return null;
              }

              return <InfiniteScrollLoader />;
            },
          }}
        />
      )}
    </PageWithFiltersWrapper>
  );
};

export default AllMemorabilia;
